import { useCallback, useEffect, useRef, useState } from 'react'
import type { ProblemFile, ResultFile, RunSummary } from '@shared/types'
import type { WebAutomation } from '@shared/web'
import { templateInfo } from '@shared/templates'
import { describeSchedule } from '@shared/automations'
import {
  download,
  explanation,
  formatSize,
  formatWhen,
  listProblems,
  listResults,
  listRuns,
  runNow as startRun
} from '../lib/api'
import DropZone from '../components/DropZone'

interface Props {
  automation: WebAutomation
  onBack: () => void
}

const RUN_STATE_LABEL: Record<RunSummary['state'], string> = {
  running: 'Running…',
  succeeded: 'Completed',
  failed: 'Had a problem'
}

export default function AutomationPage({ automation, onBack }: Props): JSX.Element {
  const info = templateInfo(automation.templateId)
  const paused = automation.paused === true
  // These come from the server: the browser is never told the automation's
  // bucket or folder layout, so it cannot work them out for itself.
  const { producesResults, setsFilesAside } = automation
  const scheduleText = paused
    ? null
    : (describeSchedule(automation.schedule) ??
      (automation.schedule ? 'Runs on a schedule set by your administrator' : null))
  const [runs, setRuns] = useState<RunSummary[] | null>(null)
  const [results, setResults] = useState<ResultFile[] | null>(null)
  const [problems, setProblems] = useState<ProblemFile[] | null>(null)
  /** Explanation text per object, fetched when the user asks for it. */
  const [explanations, setExplanations] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [runBusy, setRunBusy] = useState(false)
  /** Object currently being fetched, when a download must be confirmed. */
  const [saving, setSaving] = useState<string | null>(null)
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const [runsValue, resultsValue, problemsValue] = await Promise.all([
        listRuns(automation.jobId),
        producesResults ? listResults(automation.jobId) : Promise.resolve<ResultFile[]>([]),
        setsFilesAside ? listProblems(automation.jobId) : Promise.resolve<ProblemFile[]>([])
      ])
      setRuns(runsValue)
      setResults(resultsValue)
      setProblems(problemsValue)
      // Keep refreshing while a run is in flight so users see it finish.
      if (pollTimer.current) clearTimeout(pollTimer.current)
      if (runsValue.some((r) => r.state === 'running')) {
        pollTimer.current = setTimeout(() => void load(), 8000)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this automation.')
    }
  }, [automation.jobId, producesResults, setsFilesAside])

  useEffect(() => {
    void load()
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current)
    }
  }, [load])

  const runNow = async (): Promise<void> => {
    setRunBusy(true)
    setError('')
    setNotice('')
    try {
      await startRun(automation.jobId)
      setNotice('Started. This usually takes a minute or two — the list below will update.')
      setTimeout(() => void load(), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start a run.')
    } finally {
      setRunBusy(false)
    }
  }

  /**
   * `isResult` is false for the set-aside files below, which are never deleted
   * however the automation is configured — reading one is diagnosis, not
   * collecting a result.
   */
  const save = async (object: string, isResult: boolean): Promise<void> => {
    setError('')
    setNotice('')
    const confirmReceipt = isResult && automation.deletesAfterDownload
    if (confirmReceipt) setSaving(object)
    try {
      const { deleted, cleanupFailed } = await download(automation.jobId, object, confirmReceipt)
      if (deleted) {
        setNotice('Saved. That file has been removed and is no longer available here.')
        // The list still shows a file that is no longer there.
        void load()
      } else if (cleanupFailed) {
        // They were told the file would not be kept, and it has been.
        setError(
          'Your file was downloaded, but the cloud copy could not be removed. Tell your administrator.'
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed.')
    } finally {
      setSaving(null)
    }
  }

  const showExplanation = async (object: string): Promise<void> => {
    setError('')
    try {
      const text = await explanation(automation.jobId, object)
      setExplanations((prev) => ({ ...prev, [object]: text }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that report.')
    }
  }

  return (
    <>
      <button className="btn subtle back" onClick={onBack}>
        ← All automations
      </button>

      <div className="page-head">
        <div>
          <h1>{automation.name}</h1>
          <p className="subtitle">{info?.blurb ?? ''}</p>
          {scheduleText && <p className="schedule-note">{scheduleText}</p>}
        </div>
        <div className="head-actions">
          {paused && <span className="chip paused">Paused</span>}
          <button className="btn primary" onClick={() => void runNow()} disabled={runBusy}>
            {runBusy ? 'Starting…' : 'Process now'}
          </button>
        </div>
      </div>

      {paused && (
        <div className="alert warn">
          This automation is paused, so it has no scheduled runs right now.
          {automation.acceptsFiles
            ? ' Files you send will wait until your administrator resumes it — or press Process now to run it once.'
            : ' Press Process now to run it once, or ask your administrator to resume it.'}
        </div>
      )}
      {notice && <div className="alert info">{notice}</div>}
      {error && <div className="alert error">{error}</div>}

      {automation.acceptsFiles && (
        <section className="panel">
          <h2>Send files</h2>
          <p className="panel-note">
            {paused ? (
              'Files you drop here will wait until this automation runs again.'
            ) : (
              <>
                Files you drop here are picked up on the next run — or press <em>Process now</em> to
                handle them right away.
              </>
            )}
          </p>
          <div className={automation.drops.length > 1 ? 'drop-slots' : undefined}>
            {automation.drops.map((target) => (
              <DropZone
                key={target.slotId ?? 'default'}
                jobId={automation.jobId}
                target={target}
                onSent={() =>
                  setNotice(
                    paused
                      ? 'Files received. They will wait until this automation runs — press “Process now” to run it once.'
                      : 'Files received. Press “Process now” or wait for the next scheduled run.'
                  )
                }
              />
            ))}
          </div>
        </section>
      )}

      {producesResults && (
        <section className="panel">
          <h2>Results</h2>
          {results === null && <div className="center-note">Loading…</div>}
          {results !== null && results.length === 0 && (
            <p className="panel-note">{info?.resultsHint ?? 'Results appear here after a run.'}</p>
          )}
          {results !== null && results.length > 0 && automation.deletesAfterDownload && (
            <p className="panel-note">
              Downloading a file here removes it and the file it was made from. Only one person
              can download each result, so save it somewhere you can find again.
            </p>
          )}
          {results !== null && results.length > 0 && (
            <ul className="file-list">
              {results.map((f) => (
                <li key={f.object}>
                  <span className="file-name">{f.name}</span>
                  <span className="file-meta">
                    {formatSize(f.sizeBytes)}
                    {f.updatedAt ? ` · ${formatWhen(f.updatedAt)}` : ''}
                  </span>
                  <button
                    className="btn subtle"
                    disabled={saving !== null}
                    onClick={() => void save(f.object, true)}
                  >
                    Download
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {setsFilesAside && problems !== null && problems.length > 0 && (
        <section className="panel">
          <h2>Files that didn&rsquo;t work</h2>
          <p className="panel-note">
            These were set aside instead of being processed. Fix and re-send them, or show your
            administrator what went wrong.
          </p>
          <ul className="file-list">
            {problems.map((f) => {
              const report = f.explanationObject
              const shown = report ? explanations[report] : undefined
              return (
                <li key={f.object} className="problem-item">
                  <span className="file-name">{f.name}</span>
                  <span className="file-meta">{f.updatedAt ? formatWhen(f.updatedAt) : ''}</span>
                  {report && !shown && (
                    <button className="btn subtle" onClick={() => void showExplanation(report)}>
                      What went wrong
                    </button>
                  )}
                  <button className="btn subtle" onClick={() => void save(f.object, false)}>
                    Download
                  </button>
                  {shown && <pre className="problem-detail">{shown}</pre>}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      <section className="panel">
        <div className="panel-head">
          <h2>Recent runs</h2>
          <button className="btn subtle" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        {runs === null && <div className="center-note">Loading…</div>}
        {runs !== null && runs.length === 0 && (
          <p className="panel-note">This automation hasn&rsquo;t run yet.</p>
        )}
        {runs !== null && runs.length > 0 && (
          <>
            <ul className="run-list">
              {runs.map((r) => (
                <li key={r.id}>
                  <span className={`run-dot ${r.state}`} aria-hidden="true" />
                  <span className="run-label">{RUN_STATE_LABEL[r.state]}</span>
                  <span className="run-when">{formatWhen(r.completedAt ?? r.startedAt)}</span>
                </li>
              ))}
            </ul>
            {runs.some((r) => r.state === 'failed') && (
              <p className="panel-note run-help">
                {problems !== null && problems.length > 0
                  ? 'A run marked “Had a problem” could not finish every file — see “Files that didn’t work” above.'
                  : 'A run marked “Had a problem” hit an error partway through. If it keeps happening, tell your administrator and mention the run time — they can look up the details.'}
              </p>
            )}
          </>
        )}
      </section>
    </>
  )
}
